import React, { useEffect, useState } from 'react';
import './LearningJourneyPage.css';
import { fetchStudentLearningJourney } from '../api/studentApi';
import StudentHeader from '../components/StudentHeader';
import { useParams } from 'react-router-dom';
import ParentHeader from '../components/ParentHeader';
import { useNavigate } from 'react-router-dom';
import StudentHeaderV2 from '../components/StudentHeaderV2';

const LearningJourneyPage = ({ sid }) => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const params = useParams();

    let studentId = sid || params.id; // Get student ID from props or URL
    const userRole = JSON.parse(localStorage.getItem('user'))?.role;

    // Calculate total credits from the string values in the response
    const totalCredits = stats.reduce((acc, curr) => acc + parseInt(curr.totalCredits || 0), 0);

    // Use the backend's Mastery boolean to count mastered topics
    const masteredCount = stats.filter(s => s.isMastered).length;

    // Overall Accuracy: Average of the recent accuracy of all explored topics
    const overallAccuracy = stats.length > 0
        ? Math.round(stats.reduce((acc, curr) => acc + curr.recentAccuracy, 0) / stats.length)
        : 0;

    // Calculate overall accuracy across all topics
    // Check if stats exists and is an array before reducing
    const totalCorrect = Array.isArray(stats)
        ? stats.reduce((acc, curr) => acc + parseInt(curr.totalCorrect || 0), 0)
        : 0;

    const totalAttempted = Array.isArray(stats)
        ? stats.reduce((acc, curr) => acc + parseInt(curr.totalQuestionsAnswered || 0), 0)
        : 0;

    const accuracy = totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0;

    if (userRole === 'student') {
        studentId = JSON.parse(localStorage.getItem('user'))?.id; console.log("Parent View - Student ID:", studentId);
    }

    useEffect(() => {
        const getStats = async () => {
            try {
                setLoading(true);
                const data = await fetchStudentLearningJourney(studentId);
                setStats(data.data);
                console.log(data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (studentId) getStats();
    }, [studentId]);

    const handleLaunchQuiz = (topic) => {
        console.log(topic);
        // Navigate to the quiz page for the selected topic
        navigate(`/quiz/${topic.topicId}`, { state: { title: topic.Topic.name } });
    };

    return (
        <div className="app-layout">
            {userRole === 'parent' && (
                <ParentHeader studentId={studentId} />
            )}
            {userRole === 'student' && (
                <StudentHeaderV2/>
            )}

            <div className="progress-container">
                <header className="progress-page-header">
                    <div className="header-text">
                        <h1>{userRole === 'parent' ? "Performance Analytics" : "My Learning Journey"}</h1>
                        <p className="header-subtitle">
                            {userRole === 'parent' ? "Tracking student growth and mastery" : "Check out how much you've learned so far!"}
                        </p>
                    </div>
                    <div className="overall-accuracy-pill">
                        <div className="pill-label">Overall Accuracy</div>
                        <div className="pill-value">{overallAccuracy}%</div>
                    </div>
                </header>

                <div className="stats-summary-grid">
                    <div className="lp-stat-card masteries">
                        <div className="stat-icon">🪙</div>
                        <div className="stat-content">
                            <span className="stat-label-progress">Credits Earned</span>
                            <p className="stat-value">{totalCredits}</p>
                        </div>
                    </div>

                    <div className="lp-stat-card masteries">
                        <div className="stat-icon">📚</div>
                        <div className="stat-content">
                            <span className="stat-label-progress">Topics Explored</span>
                            <p className="stat-value">{stats.length}</p>
                        </div>
                    </div>

                    {/* Optional Third Card: Consistency or Rank */}
                    <div className="lp-stat-card masteries">
                        <div className="stat-icon">🏆</div>
                        <div className="stat-content">
                            <span className="stat-label-progress">Concepts Mastered</span>
                            <p className="stat-value">
                                {masteredCount} / {stats.length}
                            </p>
                            {/* Add this sub-label for clarity */}
                            <span className="stat-hint">Score of 80% or higher</span>
                        </div>
                    </div>
                    <div className="lp-stat-card masteries">
                        <div className="stat-icon">🧠</div>
                        <div className="stat-content">
                            <span className="stat-label-progress">Quizzes Completed</span>
                            <p className="stat-value">
                                {stats[0]?.quizzes || ''}
                            </p>
                        </div>
                    </div>
                    <div className="lp-stat-card masteries">
                        <div className="stat-icon">🥇</div>
                        <div className="stat-content">
                            <span className="stat-label-progress">Badges Unlocked</span>
                            <p className="stat-value">
                                {stats[0]?.badges || ''}
                            </p>
                        </div>
                    </div>
                </div>

                <section className="topics-breakdown">
                    <div className="section-header">
                        <h2>Topic Performance</h2>
                    </div>

                    <div className="table-wrapper">
                        <table className="custom-progress-table">
                            <thead className="custom-progress-thead">
                                <tr>
                                    <th>Learning Topic</th>
                                    <th>Attempts</th>
                                    <th>Accuracy</th>
                                    <th>Rewards</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.map((topic) => (
                                    <tr key={topic.topicId}>
                                        <td className="topic-cell">
                                            <div className="topic-content">
                                                <span className="subject-name">{topic.Topic?.Subject?.name}</span>
                                                <span className="topic-display-name">{topic.Topic?.name}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <span className="attempt-count">{topic.totalAttempts}</span>
                                            <span className="unit"> times</span>
                                        </td>

                                        <td>
                                            <div className="accuracy-container">
                                                <div className="accuracy-header">
                                                    {/* Displaying Recent Accuracy instead of All-Time */}
                                                    <span className={`accuracy-val ${topic.recentAccuracy >= 80 ? 'green' : 'orange'}`}>
                                                        {topic.recentAccuracy}%
                                                    </span>
                                                    {topic.isMastered && <span className="mastery-badge">🏆 Mastered</span>}
                                                </div>
                                                <div className="progress-bar-bg">
                                                    <div
                                                        className={`progress-bar-fill ${topic.recentAccuracy >= 80 ? 'green' : 'orange'}`}
                                                        style={{ width: `${topic.recentAccuracy}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="rewards-cell">
                                                <span className="credits-won">+{topic.totalCredits}</span>
                                                <span className="unit">Total Earned</span>
                                            </div>
                                        </td>
                                        <td>
                                            <button className="btn-primary-small" onClick={() => handleLaunchQuiz(topic)}>
                                                Launch Quiz
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>

    );
};

export default LearningJourneyPage;